import type { KeyboardEvent } from 'react';
import {
  SALES_EMPTY_TEXT,
  appointmentMachine,
  formatDateTime,
  formatEuro,
  formatInteger,
  formatPercent,
  type ControllingSales,
  type SalesAppointmentRow,
  type SalesRow,
} from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { EmptyState, Metric, Pill, Progress, TextLink, type PillVariant } from '../../../ds/components';
import figures from '../figures/figures.module.css';
import styles from './sales.module.css';

/**
 * Content of „Vertrieb" (D-323, D-325): KPI row, the table „Partner" (row click → the partner's appointments), the
 * setter block, „Termine von {Name}" and „Nächste Schritte". Presentational — the page owns query and URL.
 */

const DASH = '—';

export const SALES_TEXTS = {
  partnerTitle: 'Partner',
  setterTitle: 'Setter',
  appointmentsTitle: (name: string) => `Termine von ${name}`,
  appointmentsEmpty: 'Keine Termine im Zeitraum.',
  nextStepsTitle: 'Nächste Schritte',
  nextStepsEmpty: 'Noch keine Termine mit Ergebnis.',
} as const;

const statusTones: Record<SalesAppointmentRow['status'], PillVariant> = {
  booked: 'info',
  rescheduled: 'neutral',
  cancelled: 'neutral',
  attended: 'success',
  no_show: 'warning',
};

function quote(value: number | null): string {
  return value === null ? DASH : formatPercent(value, 0);
}

export function hasSalesData(data: ControllingSales): boolean {
  const t = data.totals;
  return t.booked + t.dealsNew + t.reserved + t.notarized > 0 || data.setters.length > 0;
}

function NumberCell({ label, value }: { label: string; value: string }) {
  return (
    <span className={clsx(figures.cell, figures.cellNumber)} role="cell">
      <span className={figures.cellKey}>{label}</span>
      <span>{value}</span>
    </span>
  );
}

const partnerColumns = [
  'Termine',
  'Wahrgenommen',
  'No-Show',
  'Quote',
  'Neue Deals',
  'Reserviert',
  'Beurkundet',
  'Volumen',
] as const;

function partnerValues(row: SalesRow): string[] {
  return [
    formatInteger(row.booked),
    formatInteger(row.attended),
    formatInteger(row.noShow),
    quote(row.showRatePct),
    formatInteger(row.dealsNew),
    formatInteger(row.reserved),
    formatInteger(row.notarized),
    formatEuro(row.volumeCents),
  ];
}

export function SalesKpis({ totals }: { totals: SalesRow }) {
  return (
    <div className={styles.kpis}>
      <Metric label="Termine" value={formatInteger(totals.booked)} />
      <Metric label="Wahrgenommen" value={formatInteger(totals.attended)} />
      <Metric label="No-Show" value={formatInteger(totals.noShow)} />
      <Metric label="Show-Quote" value={quote(totals.showRatePct)} variant="dark" />
      <Metric
        label="Beurkundet"
        value={formatInteger(totals.notarized)}
        delta={totals.notarized > 0 ? formatEuro(totals.volumeCents) : undefined}
      />
    </div>
  );
}

export function PartnerTable({
  rows,
  totals,
  selectedUserId,
  onSelect,
}: {
  rows: readonly SalesRow[];
  totals: SalesRow;
  selectedUserId: string | null;
  onSelect: (userId: string | null) => void;
}) {
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>, userId: string | null) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onSelect(userId);
  }
  return (
    <section className={clsx(figures.card, figures.tableCard)} aria-labelledby="sales-partner-title">
      <h2 id="sales-partner-title" className={figures.cardTitle}>
        {SALES_TEXTS.partnerTitle}
      </h2>
      <div className={figures.table} role="table" aria-labelledby="sales-partner-title">
        <div className={clsx(figures.tableRow, figures.tableHead, styles.partnerRow)} role="row">
          <span className={figures.cell} role="columnheader">
            Partner
          </span>
          {partnerColumns.map((label) => (
            <span key={label} className={clsx(figures.cell, figures.cellNumber)} role="columnheader">
              {label}
            </span>
          ))}
        </div>
        {rows.map((row) => {
          const selected = row.userId === selectedUserId;
          const values = partnerValues(row);
          return (
            <div
              key={row.userId ?? 'total'}
              className={clsx(
                figures.tableRow,
                styles.partnerRow,
                styles.clickable,
                selected && styles.selected,
              )}
              role="row"
              tabIndex={0}
              aria-selected={selected}
              data-user-id={row.userId ?? undefined}
              onClick={() => onSelect(selected ? null : row.userId)}
              onKeyDown={(event) => onKeyDown(event, selected ? null : row.userId)}
            >
              <span className={clsx(figures.cell, figures.cellName)} role="cell">
                {row.name}
                {row.role ? <span className={styles.role}>{row.role}</span> : null}
              </span>
              {partnerColumns.map((label, index) => (
                <NumberCell key={label} label={label} value={values[index] ?? DASH} />
              ))}
            </div>
          );
        })}
        {rows.length > 1 ? (
          <div className={clsx(figures.tableRow, styles.partnerRow, styles.totalRow)} role="row">
            <span className={clsx(figures.cell, figures.cellName)} role="cell">
              {totals.name}
            </span>
            {partnerValues(totals).map((value, index) => (
              <NumberCell key={partnerColumns[index]} label={partnerColumns[index] ?? ''} value={value} />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function SetterTable({ rows }: { rows: ControllingSales['setters'] }) {
  if (rows.length === 0) return null;
  return (
    <section className={clsx(figures.card, figures.tableCard)} aria-labelledby="sales-setter-title">
      <h2 id="sales-setter-title" className={figures.cardTitle}>
        {SALES_TEXTS.setterTitle}
      </h2>
      <div className={figures.table} role="table" aria-labelledby="sales-setter-title">
        <div className={clsx(figures.tableRow, figures.tableHead, styles.setterRow)} role="row">
          <span className={figures.cell} role="columnheader">
            Setter
          </span>
          {['Termine', 'Wahrgenommen', 'No-Show', 'Quote'].map((label) => (
            <span key={label} className={clsx(figures.cell, figures.cellNumber)} role="columnheader">
              {label}
            </span>
          ))}
        </div>
        {rows.map((row) => (
          <div key={row.userId} className={clsx(figures.tableRow, styles.setterRow)} role="row">
            <span className={clsx(figures.cell, figures.cellName)} role="cell">
              {row.name}
            </span>
            <NumberCell label="Termine" value={formatInteger(row.booked)} />
            <NumberCell label="Wahrgenommen" value={formatInteger(row.attended)} />
            <NumberCell label="No-Show" value={formatInteger(row.noShow)} />
            <NumberCell label="Quote" value={quote(row.showRatePct)} />
          </div>
        ))}
      </div>
    </section>
  );
}

export function AppointmentList({ name, rows }: { name: string; rows: readonly SalesAppointmentRow[] }) {
  return (
    <section className={clsx(figures.card, figures.tableCard)} aria-labelledby="sales-appointments-title">
      <h2 id="sales-appointments-title" className={figures.cardTitle}>
        {SALES_TEXTS.appointmentsTitle(name)}
      </h2>
      <ul className={styles.appointments}>
        {rows.length === 0 ? (
          <li className={styles.appointmentEmpty}>{SALES_TEXTS.appointmentsEmpty}</li>
        ) : null}
        {rows.map((row) => (
          <li key={row.id} className={styles.appointment}>
            <span className={styles.appointmentAt}>{formatDateTime(row.at)}</span>
            <span className={styles.appointmentContact}>
              {row.contactId !== null && row.contactName !== null ? (
                <TextLink to={`/contacts/${row.contactId}`}>{row.contactName}</TextLink>
              ) : (
                (row.contactName ?? DASH)
              )}
            </span>
            <span className={styles.appointmentPills}>
              <Pill variant={statusTones[row.status]}>{appointmentMachine.labels[row.status]}</Pill>
              {row.dealStage !== null ? (
                row.dealId !== null ? (
                  <TextLink to={`/deals/${row.dealId}`} className={styles.stageLink}>
                    <Pill variant="accent">{row.dealStage}</Pill>
                  </TextLink>
                ) : (
                  <Pill variant="accent">{row.dealStage}</Pill>
                )
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function NextSteps({ steps }: { steps: SalesRow['nextSteps'] }) {
  const total = steps.reduce((sum, step) => sum + step.count, 0);
  return (
    <section className={clsx(figures.card, figures.sideCard)} aria-labelledby="sales-next-title">
      <div id="sales-next-title" className={figures.sideEyebrow}>
        {SALES_TEXTS.nextStepsTitle}
      </div>
      <div className={styles.steps}>
        {steps.length === 0 ? <div className={figures.peopleEmpty}>{SALES_TEXTS.nextStepsEmpty}</div> : null}
        {steps.map((step) => (
          <div key={step.stage} className={styles.step}>
            <div className={styles.stepHead}>
              <span>{step.stage}</span>
              <span className={styles.stepCount}>{formatInteger(step.count)}</span>
            </div>
            <Progress
              value={total === 0 ? 0 : (step.count / total) * 100}
              label={step.stage}
              variant="thin"
            />
          </div>
        ))}
      </div>
    </section>
  );
}

export function SalesView({
  data,
  selectedUserId,
  onSelect,
}: {
  data: ControllingSales;
  selectedUserId: string | null;
  onSelect: (userId: string | null) => void;
}) {
  if (!hasSalesData(data)) {
    return <EmptyState eyebrow="Vertrieb" title={SALES_EMPTY_TEXT} />;
  }
  const selectedRow =
    data.rows.find((row) => row.userId === (selectedUserId ?? data.appointmentsOf?.userId)) ?? null;
  const steps = selectedRow?.nextSteps ?? data.totals.nextSteps;
  return (
    <div className={styles.content}>
      <SalesKpis totals={data.totals} />
      <div className={figures.grid}>
        <div className={figures.column}>
          <PartnerTable
            rows={data.rows}
            totals={data.totals}
            selectedUserId={selectedUserId}
            onSelect={onSelect}
          />
          {data.appointmentsOf !== null ? (
            <AppointmentList name={data.appointmentsOf.name} rows={data.appointments} />
          ) : null}
        </div>
        <div className={figures.column}>
          <NextSteps steps={steps} />
          <SetterTable rows={data.setters} />
        </div>
      </div>
    </div>
  );
}
